import { Head } from '@inertiajs/react';
import { motion } from 'framer-motion';
import Layout from '@/layouts/Layout';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import { Heading } from '@/components/ui/heading';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck,
  Globe,
  Users,
  Plane,
  Lock,
  Bot,
  BadgeCheck,
  AlertCircle,
  RefreshCw,
  Building2,
  FileText,
  Mail,
  Eye,
  Clock,
  type LucideIcon,
} from 'lucide-react';

const SectionHeader = ({
  icon: Icon,
  number,
  title,
}: {
  icon: LucideIcon;
  number: string;
  title: string;
}) => (
  <Heading level={2} icon={Icon} className="mb-5">
    <span className="text-muted-foreground mr-2">{number}</span>
    {title}
  </Heading>
);

const purposesTitolare = [
  { purpose: 'Gestione account e autenticazione del Professionista', basis: 'Esecuzione del contratto (art. 6.1.b)' },
  { purpose: 'Fatturazione e gestione abbonamenti', basis: 'Esecuzione del contratto (art. 6.1.b)' },
  { purpose: 'Adempimenti fiscali e contabili', basis: 'Obbligo legale (art. 6.1.c)' },
  { purpose: 'Sicurezza della piattaforma e prevenzione abusi', basis: 'Legittimo interesse (art. 6.1.f) — garantire la sicurezza e l\'integrità della piattaforma' },
  { purpose: 'Comunicazioni di servizio', basis: 'Esecuzione del contratto (art. 6.1.b)' },
  { purpose: 'Analisi aggregate di utilizzo della piattaforma', basis: 'Legittimo interesse (art. 6.1.f) — migliorare il servizio sulla base di dati aggregati e anonimizzati' },
];

const purposesResponsabile = [
  {
    purpose: 'Archiviazione e consultazione di chat, diari e note cliniche',
    basis: 'Finalità di cura sotto la responsabilità del Professionista (art. 9.2.h)',
  },
  {
    purpose: 'Generazione di riassunti e analisi tramite AI a supporto dell\'attività clinica',
    basis: 'Finalità di cura sotto la responsabilità del Professionista (art. 9.2.h)',
  },
  {
    purpose: 'Monitoraggio di sicurezza (Safety Team) per identificazione di situazioni a rischio',
    basis: 'Interesse vitale dell\'interessato (art. 6.1.d e art. 9.2.c)',
  },
];

const accessTable = [
  { subject: 'Professionista', data: 'Tutti i dati dei propri pazienti, inclusi i riassunti generati dall\'AI' },
  { subject: 'Paziente', data: 'I propri dati personali, chat, diari. Non i riassunti AI' },
  { subject: 'Team Emovia (amministrazione)', data: 'Solo dati aggregati e anonimizzati' },
  {
    subject: 'Safety Team Emovia',
    data: 'Accesso puntuale a specifici contenuti, esclusivamente per la tutela della sicurezza degli utenti',
  },
];

const recipients = [
  { name: 'Amazon Web Services Inc.', service: 'Infrastruttura', location: 'Unione Europea' },
  { name: 'Cloudflare Inc.', service: 'CDN e sicurezza', location: 'Stati Uniti *' },
  { name: 'Google LLC', service: 'Archiviazione file ed email', location: 'Unione Europea' },
  { name: 'Hetzner Online GmbH', service: 'Hosting', location: 'Unione Europea' },
  { name: 'OpenAI LP', service: 'Elaborazione AI', location: 'Stati Uniti *' },
  { name: 'PostHog Inc.', service: 'Analytics', location: 'Unione Europea' },
  { name: 'Resend Inc.', service: 'Email transazionali', location: 'Stati Uniti *' },
  { name: 'Slack Technologies LLC', service: 'Notifiche operative interne', location: 'Stati Uniti *' },
  { name: 'Stripe Inc.', service: 'Gestione pagamenti', location: 'Stati Uniti *' },
];

const retentionTable = [
  { category: 'Dati account Professionista', period: 'Per la durata del contratto, poi 10 anni per obblighi fiscali' },
  { category: 'Dati di fatturazione', period: '10 anni dalla transazione (obblighi fiscali)' },
  {
    category: 'Dati dei pazienti (chat, diari, note)',
    period: 'Per la durata del rapporto terapeutico, poi fino a 5 anni dalla conclusione, salvo diversa istruzione del Professionista',
  },
  { category: 'Riassunti AI', period: 'Come i dati dei pazienti. La retention sui sistemi OpenAI è conforme al relativo DPA.' },
  { category: 'Log tecnici e di sicurezza', period: '24 mesi' },
  { category: 'Backup', period: 'Massimo 30 giorni dalla cancellazione dei dati originali' },
];

const rights = [
  { art: 'Art. 15', label: 'Accesso', desc: 'Ottenere conferma del trattamento e copia dei tuoi dati.' },
  { art: 'Art. 16', label: 'Rettifica', desc: 'Chiedere la correzione di dati inesatti o incompleti.' },
  { art: 'Art. 17', label: 'Cancellazione', desc: 'Chiedere la cancellazione dei tuoi dati.' },
  { art: 'Art. 18', label: 'Limitazione', desc: 'Chiedere la limitazione del trattamento.' },
  { art: 'Art. 20', label: 'Portabilità', desc: 'Ricevere i tuoi dati in formato strutturato.' },
  { art: 'Art. 21', label: 'Opposizione', desc: 'Opporti al trattamento basato su legittimo interesse.' },
  {
    art: '—',
    label: 'Revoca del consenso',
    desc: 'In qualsiasi momento, senza pregiudicare i trattamenti precedenti.',
  },
];

const PlatformPrivacyPolicy = () => {
  return (
    <Layout>
      <Head title="Informativa Privacy — Piattaforma Emovia" />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-blue-lighter/30">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }} className="mb-6">
              <Badge
                variant="outline"
                className="bg-primary/10 border-primary/20 text-primary/80 rounded-full px-4 py-1.5"
              >
                Ultimo aggiornamento: 25 marzo 2026
              </Badge>
            </motion.div>
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={1} font="display" className="text-primary mb-3">
                Informativa sul trattamento dei dati personali — Piattaforma Emovia
              </Heading>
            </motion.div>
            <motion.p variants={fadeInUp} transition={{ duration: 0.5 }} className="text-lg text-muted-foreground">
              ai sensi degli artt. 13-14 del Regolamento (UE) 2016/679 (&ldquo;GDPR&rdquo;)
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">

            {/* 1. Titolare e Responsabile */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Building2} number="1." title="Titolare e Responsabile del trattamento" />
              <div className="space-y-4 text-muted-foreground">
                <p>
                  La piattaforma Emovia è utilizzata da professionisti psicologi iscritti agli albi professionali
                  (di seguito &ldquo;Professionista&rdquo;), i quali agiscono in qualità di{' '}
                  <span className="text-foreground">Titolari autonomi del trattamento</span> dei dati personali e
                  sanitari dei propri pazienti.
                </p>
                <p>
                  <span className="text-foreground">Emovia di Daniele Di Bernardo</span> agisce in qualità di{' '}
                  <span className="text-foreground">Responsabile del trattamento (art. 28 GDPR)</span> per conto del
                  Professionista, limitatamente ai trattamenti necessari all&apos;erogazione dei servizi della
                  piattaforma.
                </p>
                <div className="border border-border rounded-lg p-4 space-y-1">
                  <p className="text-foreground font-medium">Emovia di Daniele Di Bernardo</p>
                  <p>Viale Sicilia 31, 27100 Pavia (PV), Italia</p>
                  <p>P.IVA: IT03023520186</p>
                  <a
                    href="mailto:privacy@emovia.com"
                    aria-label="Invia email a privacy@emovia.com"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline mt-1"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    privacy@emovia.com
                  </a>
                </div>
                <p>
                  Per i dati relativi alla gestione del proprio account, alla fatturazione e alla fruizione della
                  piattaforma, Emovia agisce in qualità di{' '}
                  <span className="text-foreground">Titolare del trattamento</span>.
                </p>
              </div>
            </motion.div>

            {/* 2. Categorie di dati */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={FileText} number="2." title="Categorie di dati trattati" />
              <div className="space-y-6 text-muted-foreground">
                <div>
                  <Heading level={3} className="mb-3">
                    2.1 Dati del Professionista (Emovia come Titolare)
                  </Heading>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><span className="text-foreground">Dati identificativi:</span> nome, cognome, email, telefono</li>
                    <li><span className="text-foreground">Dati professionali:</span> numero iscrizione albo, specializzazione</li>
                    <li><span className="text-foreground">Dati di autenticazione:</span> credenziali di accesso, token OAuth</li>
                    <li><span className="text-foreground">Dati di fatturazione:</span> stato abbonamento, storico transazioni</li>
                    <li><span className="text-foreground">Dati di navigazione:</span> indirizzo IP, browser, dispositivo, pagine visitate</li>
                  </ul>
                </div>
                <div>
                  <Heading level={3} className="mb-3">
                    2.2 Dati dei pazienti (Emovia come Responsabile per conto del Professionista)
                  </Heading>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><span className="text-foreground">Dati identificativi:</span> nome, cognome, email</li>
                    <li>
                      <span className="text-foreground">Dati relativi alla salute (art. 9 GDPR):</span> contenuto delle
                      chat terapeutiche, diari personali, note cliniche redatte dal Professionista
                    </li>
                    <li>Riassunti e analisi generati tramite intelligenza artificiale a supporto del Professionista</li>
                    <li>File e documenti caricati nella piattaforma</li>
                  </ul>
                  <p className="mt-3 text-sm border-t border-border/50 pt-3">
                    I dati di pagamento (numero carta, IBAN) sono gestiti direttamente da Stripe e non sono memorizzati
                    da Emovia.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* 3. Natura del conferimento dei dati */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={AlertCircle} number="3." title="Natura del conferimento dei dati" />
              <p className="text-muted-foreground">
                Il conferimento dei dati personali indicati nella sezione precedente è necessario per l&apos;esecuzione
                del contratto e l&apos;utilizzo della piattaforma Emovia. Il mancato conferimento comporta
                l&apos;impossibilità di creare un account, utilizzare i servizi della piattaforma e gestire i rapporti
                terapeutici tramite Emovia.
              </p>
            </motion.div>

            {/* 4. Finalità e basi giuridiche */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={ShieldCheck} number="4." title="Finalità e basi giuridiche" />
              <div className="space-y-8">
                <div>
                  <Heading level={3} className="mb-4">
                    4.1 Trattamenti di Emovia come Titolare
                  </Heading>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-muted/40 text-left border-b border-border">
                          <th scope="col" className="px-4 py-3 text-foreground">Finalità</th>
                          <th scope="col" className="px-4 py-3 text-foreground">Base giuridica</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {purposesTitolare.map(({ purpose, basis }) => (
                          <tr key={purpose} className="hover:bg-muted/20 transition-colors">
                            <td className="px-4 py-3 text-foreground">{purpose}</td>
                            <td className="px-4 py-3 text-muted-foreground">{basis}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <Heading level={3} className="mb-3">
                    4.2 Trattamenti di Emovia come Responsabile
                  </Heading>
                  <p className="text-muted-foreground mb-4">
                    I dati dei pazienti sono trattati da Emovia esclusivamente per le finalità indicate dal
                    Professionista e nel rispetto delle istruzioni documentate nel Data Processing Agreement (DPA). In
                    particolare:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-muted/40 text-left border-b border-border">
                          <th scope="col" className="px-4 py-3 text-foreground">Finalità</th>
                          <th scope="col" className="px-4 py-3 text-foreground">Base giuridica (del Professionista)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {purposesResponsabile.map(({ purpose, basis }) => (
                          <tr key={purpose} className="hover:bg-muted/20 transition-colors">
                            <td className="px-4 py-3 text-foreground">{purpose}</td>
                            <td className="px-4 py-3 text-muted-foreground">{basis}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* 5. Accesso e visibilità */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Eye} number="5." title="Accesso ai dati e visibilità" />
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Soggetto</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Dati accessibili</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {accessTable.map(({ subject, data }) => (
                      <tr key={subject} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{subject}</td>
                        <td className="px-4 py-3 text-muted-foreground">{data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground text-sm">
                Nessun operatore Emovia accede ai dati sanitari per finalità diverse dalla sicurezza degli utenti.
              </p>
            </motion.div>

            {/* 6. Destinatari */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Users} number="6." title="Destinatari dei dati" />
              <p className="text-muted-foreground mb-6">
                I dati possono essere comunicati ai seguenti Responsabili del trattamento (o sub-responsabili ex
                art. 28 GDPR), sulla base di specifici accordi (DPA):
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Fornitore</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Servizio</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Sede server</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {recipients.map(({ name, service, location }) => (
                      <tr key={name} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{service}</td>
                        <td className="px-4 py-3 text-muted-foreground">{location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground mt-4 text-sm">
                I dati non vengono venduti né ceduti a terzi. I dati inviati a OpenAI tramite API non vengono
                utilizzati per l&apos;addestramento dei modelli.
              </p>
            </motion.div>

            {/* 7. Trasferimenti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Plane} number="7." title="Trasferimenti verso Paesi terzi" />
              <p className="text-muted-foreground mb-4">
                I fornitori contrassegnati con (*) hanno sede negli Stati Uniti. I trasferimenti avvengono sulla base di:
              </p>
              <ul className="space-y-3 text-muted-foreground mb-4">
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">Clausole Contrattuali Standard (SCC)</span>{' '}
                    approvate dalla Commissione Europea (decisione 2021/914).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">Data Processing Agreement (DPA)</span>{' '}
                    sottoscritto con ciascun fornitore.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">EU-US Data Privacy Framework</span>{' '}
                    (decisione di adeguatezza 2023/1795), ove applicabile al fornitore.
                  </span>
                </li>
              </ul>
              <p className="text-muted-foreground text-sm border-t border-border/50 pt-4">
                Per i dati relativi alla salute elaborati tramite OpenAI: i dati vengono trasmessi tramite API con
                contratto enterprise; la data retention sui sistemi OpenAI è conforme al relativo DPA; i dati non
                vengono utilizzati per l&apos;addestramento dei modelli di intelligenza artificiale.
              </p>
            </motion.div>

            {/* 8. Conservazione */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Clock} number="8." title="Conservazione dei dati" />
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Categoria</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Periodo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {retentionTable.map(({ category, period }) => (
                      <tr key={category} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{category}</td>
                        <td className="px-4 py-3 text-muted-foreground">{period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground text-sm">
                Al termine del rapporto con il Professionista, i dati dei pazienti vengono restituiti o cancellati
                secondo le istruzioni del Professionista, nel rispetto degli obblighi di legge.
              </p>
            </motion.div>

            {/* 9. Misure di sicurezza */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Lock} number="9." title="Misure di sicurezza" />
              <ul className="space-y-3 text-muted-foreground">
                {[
                  'Cifratura dei dati in transito e a riposo.',
                  'Accesso ai dati sanitari limitato al solo Professionista competente.',
                  'Autenticazione sicura con possibilità di accesso tramite OAuth.',
                  'Sistemi di monitoraggio e prevenzione degli accessi non autorizzati.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* 10. Decisioni automatizzate */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Bot} number="10." title="Decisioni automatizzate e profilazione" />
              <div className="space-y-3 text-muted-foreground">
                <p>
                  La piattaforma utilizza sistemi di intelligenza artificiale per generare riassunti e analisi a
                  supporto dell&apos;attività clinica del Professionista. Tali elaborazioni:
                </p>
                <ul className="space-y-2">
                  {[
                    'Sono strumenti di supporto e non sostituiscono il giudizio clinico del Professionista.',
                    'Non producono decisioni automatizzate con effetti giuridici sui pazienti.',
                    'Sono visibili esclusivamente al Professionista.',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  Le elaborazioni AI vengono generate tramite il servizio di OpenAI (indicato tra i destinatari dei
                  dati). Il Professionista mantiene il pieno controllo sull&apos;utilizzo di tali strumenti e può
                  disattivarli in qualsiasi momento. Nessuna decisione clinica viene presa in modo automatizzato dalla
                  piattaforma.
                </p>
              </div>
            </motion.div>

            {/* 11. Diritti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={BadgeCheck} number="11." title="I tuoi diritti" />
              <ul className="divide-y divide-border/60 mb-6">
                {rights.map(({ art, label, desc }) => (
                  <li key={label} className="flex items-baseline gap-4 py-3">
                    <span className="text-muted-foreground text-sm w-16 flex-shrink-0">{art}</span>
                    <div>
                      <span className="text-foreground">{label}</span>
                      <p className="text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  Per i dati trattati da Emovia come Titolare: scrivi a{' '}
                  <a
                    href="mailto:privacy@emovia.com"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    privacy@emovia.com
                  </a>
                  .
                </p>
                <p>
                  Per i dati trattati da Emovia come Responsabile per conto del Professionista: rivolgiti al tuo
                  Professionista, che è il Titolare del trattamento.
                </p>
                <p>Risponderemo entro 30 giorni dalla richiesta.</p>
              </div>
            </motion.div>

            {/* 12. Reclamo */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={AlertCircle} number="12." title="Diritto di reclamo" />
              <p className="text-muted-foreground mb-4">
                Hai il diritto di proporre reclamo al Garante per la Protezione dei Dati Personali:
              </p>
              <div className="text-muted-foreground space-y-1">
                <p className="text-foreground">Garante per la Protezione dei Dati Personali</p>
                <p>Piazza Venezia 11, 00187 Roma</p>
                <div className="pt-2 space-y-1.5">
                  <a
                    href="https://www.garanteprivacy.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Globe className="h-4 w-4" aria-hidden />
                    www.garanteprivacy.it
                  </a>
                  <a
                    href="mailto:protocollo@gpdp.it"
                    aria-label="Invia email a protocollo@gpdp.it"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    protocollo@gpdp.it
                  </a>
                  <a
                    href="mailto:protocollo@pec.gpdp.it"
                    aria-label="Invia email a protocollo@pec.gpdp.it"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    protocollo@pec.gpdp.it (PEC)
                  </a>
                </div>
              </div>
            </motion.div>

            {/* 13. Modifiche */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10"
            >
              <SectionHeader icon={RefreshCw} number="13." title="Modifiche alla presente informativa" />
              <p className="text-muted-foreground">
                Il Titolare si riserva di aggiornare la presente informativa. La data di ultimo aggiornamento è
                indicata in cima al documento. In caso di modifiche sostanziali, ne daremo comunicazione tramite la
                piattaforma.
              </p>
            </motion.div>

          </div>

          {/* Contatti privacy */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto mt-10"
          >
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
              <Mail className="h-7 w-7 text-primary mx-auto mb-4" aria-hidden />
              <Heading level={3} className="mb-2">
                Hai domande sulla tua privacy?
              </Heading>
              <p className="text-muted-foreground mb-5">Scrivici. Ti rispondiamo entro 30 giorni.</p>
              <a
                href="mailto:privacy@emovia.com"
                aria-label="Invia email a privacy@emovia.com"
                className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
              >
                <Mail className="h-4 w-4" aria-hidden />
                privacy@emovia.com
              </a>
            </div>
          </motion.div>

        </div>
      </section>
    </Layout>
  );
};

export default PlatformPrivacyPolicy;
